<template>
  <div>
    <b-row v-if="task !== null">
      <b-col cols="12">
        <!-- Payment Details -->
        <b-row class="mt-2 mb-4">
          <b-col cols="12">
            <PRNViewer :tracking-number="task.data.trackingNumber || task.data.applicationId" />
          </b-col>
        </b-row>

        <b-row class="mx-auto my-2">
          <b-col cols="12 p-0">
            <table class="min-w-full leading-normal mt-1 text-gray" style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2">CopyrightAmendment Details</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Tracking Number</th>
                <th class="px-2 py-1">Title</th>
                <th class="px-2 py-1">Description</th>
                <th class="px-2 py-1">Is Derivative</th>
                <th class="px-2 py-1">Status</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" >
                <td class="px-2 py-1 font-bold text-gray">
                     <span class="">{{ task.data.trackingNumber || task.data.applicationId }}</span>
                </td>
                <td class="px-2 py-1">
                  <span class="text-gray">{{ (task.data.originalWork && task.data.originalWork.title) || task.data.title || (task.data.originalWork && task.data.originalWork.workTypes && task.data.originalWork.workTypes[0] ? task.data.originalWork.workTypes[0].name : '') }}</span>
                </td>
                <td class="px-2 py-1">
                  <span class="text-gray">{{ getDescription(task.data.description) || (task.data.originalWork && task.data.originalWork.description) || (task.data.originalWork && task.data.originalWork.workTypes && task.data.originalWork.workTypes[0] ? task.data.originalWork.workTypes[0].description : '') }}</span>
                </td>
                <td class="px-2 py-1">
                  <span class="text-gray">{{ isDerivative(task.data.originalWork ? task.data.originalWork.isDerivativeWork : task.data.isDerivativeWork) }}</span>
                </td>
                <td class="px-2 py-1">
                  <span :class="statusColorCode(task.data.status)">{{statusDataInfo(task.data.status)}}</span>
                </td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Work Types -->
        <b-row class="mt-2 mb-4" v-if="(task.data.originalWork && task.data.originalWork.workTypes && task.data.originalWork.workTypes.length > 0) || (task.data.workTypes && task.data.workTypes.length > 0)">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray"
                   style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Work Types</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Name</th>
                <th class="px-2 py-1">Description</th>
                <th class="px-2 py-1">Sub Work Type</th>
                <th class="px-2 py-1">Sub Work Type Description</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" v-for="(wt, idx) in ((task.data.originalWork && task.data.originalWork.workTypes) || task.data.workTypes)" :key="idx">
                <td class="px-2 py-1">{{wt.name}}</td>
                <td class="px-2 py-1">{{wt.description}}</td>
                <td class="px-2 py-1">{{wt.subWorkType ? wt.subWorkType.name : 'N/A'}}</td>
                <td class="px-2 py-1">{{wt.subWorkType ? wt.subWorkType.description : 'N/A'}}</td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Proposed Changes Table -->
        <b-row class="mt-2 mb-4" v-if="task.data.proposedChanges && task.data.proposedChanges.length > 0">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray"
                   style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Proposed Changes</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Entity Type</th>
                <th class="px-2 py-1">Change Type</th>
                <th class="px-2 py-1">Changes</th>
                <th class="px-2 py-1">Status</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" v-for="(change, idx) in task.data.proposedChanges" :key="idx">
                <td class="px-2 py-1 text-capitalize">{{ change.entityType ? change.entityType.toLowerCase() : '' }}</td>
                <td class="px-2 py-1 text-capitalize">{{ change.changeType ? change.changeType.toLowerCase() : '' }}</td>
                <td class="px-2 py-1">{{ formatFieldChanges(change.fieldChanges) }}</td>
                <td class="px-2 py-1 text-capitalize">{{ change.status ? change.status.toLowerCase() : 'Pending' }}</td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Fallback Ownerships -->
        <b-row class="mt-2 mb-4" v-if="(!task.data.proposedChanges || task.data.proposedChanges.length === 0) && (task.data.ownerships && task.data.ownerships.length > 0)">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray"
                   style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Ownerships / Rights Holders</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Name</th>
                <th class="px-2 py-1">Type</th>
                <th class="px-2 py-1">ID Number</th>
                <th class="px-2 py-1">Is Owner</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" v-for="(owner, idx) in task.data.ownerships" :key="idx">
                <td class="px-2 py-1 text-capitalize">{{owner.holderName}}</td>
                <td class="px-2 py-1 text-capitalize">{{owner.holderType.toLowerCase()}}</td>
                <td class="px-2 py-1">{{owner.idNumber}}</td>
                <td class="px-2 py-1">{{owner.isOwner ? 'Yes' : 'No'}}</td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Applicant Details -->
        <b-row class="mt-2 mb-4" v-if="task.data.applicant">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-blue-dark"
                   style="border: solid 1px #bdd7f1;">
              <thead>
              <tr class="bg-blue-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Applicant Details</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Name</th>
                <th class="px-2 py-1">Email</th>
                <th class="px-2 py-1">Phone</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #bdd7f1;">
                <td class="px-2 py-1 text-capitalize">
                  {{ [task.data.applicant.firstName, task.data.applicant.givenName, task.data.applicant.middleName, task.data.applicant.lastName, task.data.applicant.surname].filter(Boolean).join(' ') }}
                </td>
                <td class="px-2 py-1">{{task.data.applicant.email}}</td>
                <td class="px-2 py-1">{{task.data.applicant.phoneNumber}}</td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

      </b-col>
    </b-row>
  </div>
</template>
 
<script>
import moment from "moment";
import PRNViewer from "@/views/home/commons/PRNViewer.vue";
 
export default {
  name: "CopyrightAmendmentApplication",
  components: { PRNViewer },
  props:{
    task: {
      type: [Object, null],
      default: null,
    }
  },
  data(){
    return{
      hasComments: false,
      currentRoute: "form",
      selectedId:null,
      margin:"2px",
      hasContent:false,
    }
  },
  methods:{
    isDerivative(val) {
      if (typeof val === 'object' && val !== null) {
        return val.Valid && val.Bool ? 'Yes' : 'No';
      }
      return val ? 'Yes' : 'No';
    },
    getDescription(val) {
      if (typeof val === 'object' && val !== null) {
        return val.Valid ? val.String : '';
      }
      return val || '';
    },
    statusDataInfo(status) {
      if (!status) return "";
      if (status.toUpperCase() === "APPROVED") {
        return "Approved"
      } else if (status.toUpperCase() === "RESERVED") {
        return "Reserved"
      } else if (status.toUpperCase() === "QUERIED") {
        return "Issues Raised"
      } else if (status.toUpperCase() === "ASSIGNED") {
        return "Being Processed"
      } else if (status.toUpperCase() === "PENDING") {
        return "Submitted"
      } else if (status.toUpperCase() === "APPEALED") {
        return "Appealed"
      }else if (status.toUpperCase() === "REJECTED") {
        return "Rejected"
      }else if (status.toUpperCase() === "DRAFT") {
        return "Draft"
      } else if (status.toUpperCase() === "AWAITING_PAYMENT_CONFIRMATION") {
        return "Confirming Payment"
      }else if (status.toUpperCase() === "PAYMENT_FAILED") {
        return "Payment Failed"
      }
      return status;
    },
    statusColorCode(status) {
      if (!status) return "";
      if (status.toUpperCase() === "APPROVED" || status.toUpperCase() === "RESERVED") {
        return "text-green"
      } else if (status.toUpperCase() === "QUERIED") {
        return "text-orange"
      } else if (status.toUpperCase() === "ASSIGNED") {
        return "text-blue"
      } else if (status.toUpperCase() === "PENDING") {
        return "text-gray"
      } else if (status.toUpperCase() === "APPEALED" ) {
        return "text-red"
      }else if (status.toUpperCase() === "REJECTED") {
        return "text-red"
      }else if (status.toUpperCase() === "DRAFT") {
        return "text-gray"
      } else if (status.toUpperCase() === "AWAITING_PAYMENT_CONFIRMATION") {
        return "text-purple"
      }else if (status.toUpperCase() === "PAYMENT_FAILED") {
        return "text-red"
      }
      return "";
    },
    dateFormat(date){
      return moment(date).format("DD-MM-YYYY")
    },
    formatFieldChanges(changes) {
      if (!changes) return "";
      let parsed = changes;
      if (typeof changes === 'string') {
        try {
          parsed = JSON.parse(changes);
        } catch (e) {
          return changes;
        }
      }
      return Object.entries(parsed)
        .map(([key, val]) => `${key}: ${val}`)
        .join(", ");
    },
  },
}
</script>

<style scoped>
th, td{
  font-size: 12px;
}
</style>
